import {
  isBlockWithChildren,
  type BlockWithChildren,
  isHidableBlock,
  isRichTextBlock,
  isListBlock,
  blockRelationshipBaseOps,
  shouldForcefullyPropagateHiddenState,
} from '@basehub/data/replicache/index'
import type { WriteTransaction } from 'replicache'
import { invariant } from '@epic-web/invariant'
import { idToSourceBlockId } from './copy-on-write'
import { normalizeStringPath } from '@basehub/data/replicache/index'
import { BlockBatchMutationParams } from './batch'
import { updateBlock } from './crud'
import {
  blockBaseOps,
  ensureBaseHubTransaction,
} from '@basehub/data/replicache/tx/iso'
import { generateKeyBetween } from '@basehub/data/replicache/mutators/repo-space/block/helpers/fractional-indexing'

/* -------------------------------------------------------------------------------------------------
 * Propagate Hidden State to Children
 * -----------------------------------------------------------------------------------------------*/

export async function propagateHiddenStateToChildren(
  tx: WriteTransaction,
  {
    block,
    hiddenValue,
    path,
    dryRun,
    isoNow,
    justRichTextChildren,
    rootBlockId,
  }: {
    block: BlockWithChildren
    dryRun: boolean
    path: string
    isoNow: string
    hiddenValue?: boolean
    justRichTextChildren?: boolean
    rootBlockId: string
  }
): Promise<{
  undo: BlockBatchMutationParams['mutations']
}> {
  ensureBaseHubTransaction(tx)

  const undo: BlockBatchMutationParams['mutations'] = []

  invariant(
    isBlockWithChildren(block),
    'block does not have children, cannot propagate hidden state.'
  )

  hiddenValue = hiddenValue ?? block.hidden

  if (!dryRun && isHidableBlock(block)) {
    const result = await updateBlock(tx, {
      id: block.id,
      instanceChildTarget: block.instanceChildTarget,
      path,
      isoNow,
      strategy: 'merge',
      data: { type: block.type, hidden: hiddenValue },
      skipPropagateHiddenState: true,
      ignoreHiddenPropagationWarning: true,
    })
    undo.push(...result.undo)
  }

  const children = await tx.getBlockChildren({
    sourceBlockId: idToSourceBlockId(block),
    rootBlockId,
  })

  const shouldForcefullyPropagate = shouldForcefullyPropagateHiddenState(block)

  for (const br of children) {
    const childBlock = await blockBaseOps.get(tx, br.blockId)
    invariant(childBlock, `child block ${br.blockId} for BR:${br.id} not found`)

    if (justRichTextChildren && !isRichTextBlock(childBlock)) {
      continue
    }

    if (
      isListBlock(block) &&
      block.typeOptions.targetBlockId === idToSourceBlockId(childBlock)
    ) {
      // is template component
      if (childBlock.hidden) {
        // don't propagate hidden state to the target block of the list
        continue
      } else if (hiddenValue) {
        // target block is turning to hidden; we pin it to the top of the list
        await blockRelationshipBaseOps.update(tx, {
          id: br.id,
          order: generateKeyBetween(
            null,
            children[0]?.order ?? null,
            rootBlockId
          ),
        })
      }
    }

    const hasChanged = childBlock.hidden !== hiddenValue
    const childPath = normalizeStringPath({
      path,
      edgeId: childBlock.id,
      format: 'string',
      includeEdge: true,
    })

    if (
      (hasChanged || shouldForcefullyPropagate) &&
      isBlockWithChildren(childBlock)
    ) {
      const result = await propagateHiddenStateToChildren(tx, {
        block: childBlock,
        hiddenValue, // propagate `hidden` here.
        dryRun: false, // for nested blocks, we always do the actual update
        path: childPath,
        isoNow,
        rootBlockId,
      })
      undo.push(...result.undo)
    }
  }

  return { undo }
}
